import React, { useState, useRef } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileCode, CheckCircle2, Trash2 } from "lucide-react";
import { BaseCard, PrimaryButton, SecondaryButton, DangerButton, Modal } from "@/components/ui/DesignSystem";

export default function BackupPage() {
  const [loading, setLoading] = useState(false);
  const [importData, setImportData] = useState(null);
  const [fileDetails, setFileDetails] = useState(null);
  const [rawJsonText, setRawJsonText] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetText, setResetText] = useState("");

  const fileInputRef = useRef(null);

  // 1. Export JSON Backup
  const downloadBackup = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.get("/admin/backup/export");
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `family_bank_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success("تم تصدير نسخة احتياطية شاملة بنجاح (.json)");
    } catch {
      toast.error("فشل تصدير النسخة الاحتياطية");
    } finally {
      setLoading(false);
    }
  };

  // 2. Handle File Upload Selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        setImportData(parsed);
        setFileDetails({
          name: file.name,
          size: (file.size / 1024).toFixed(1) + " KB",
          version: parsed.version || "1.0",
          usersCount: Array.isArray((parsed.data || parsed).users) ? (parsed.data || parsed).users.length : 0,
          txsCount: Array.isArray((parsed.data || parsed).transactions) ? (parsed.data || parsed).transactions.length : 0,
          receiptsCount: Array.isArray((parsed.data || parsed).receipts) ? (parsed.data || parsed).receipts.length : 0
        });
        toast.success(`تم قراءة الملف ${file.name} بنجاح`);
      } catch (err) {
        toast.error("ملف JSON غير صالح أو به أخطاء نسق");
      }
    };
    reader.readAsText(file);
  };

  // 3. Handle Raw JSON Text Paste
  const handleParseRawText = () => {
    if (!rawJsonText.trim()) return toast.error("أدخل نص JSON أولاً");
    try {
      const parsed = JSON.parse(rawJsonText);
      setImportData(parsed);
      setFileDetails({
        name: "إدخال يدوي مخصص (JSON)",
        size: "نص مباشر",
        version: parsed.version || "1.0",
        usersCount: Array.isArray((parsed.data || parsed).users) ? (parsed.data || parsed).users.length : 0,
        txsCount: Array.isArray((parsed.data || parsed).transactions) ? (parsed.data || parsed).transactions.length : 0,
        receiptsCount: Array.isArray((parsed.data || parsed).receipts) ? (parsed.data || parsed).receipts.length : 0
      });
      toast.success("تم تحليل نص الـ JSON بنجاح");
    } catch {
      toast.error("تنسيق JSON غير صحيح");
    }
  };

  // 4. Perform Data Import / Restore
  const executeImport = async () => {
    if (!importData) return toast.error("اختر ملف نسخة احتياطية أولاً");
    try {
      setLoading(true);
      const res = await adminApi.post("/admin/backup/import", importData);
      toast.success(res.data?.message || "تم استعادة كامل بيانات النظام بنجاح!");
      setConfirmModalOpen(false);
      setImportData(null);
      setFileDetails(null);
      setRawJsonText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت عملية استعادة البيانات");
    } finally {
      setLoading(false);
    }
  };

  // 5. Perform System Data Reset
  const executeReset = async () => {
    if (resetText !== "RESET") return toast.error("يجب كتابة الكلمة التأكيدية RESET");
    try {
      setLoading(true);
      const res = await adminApi.post("/admin/backup/reset", { confirmation: resetText });
      toast.success(res.data?.message || "تم إعادة تعيين البيانات بنجاح");
      setResetModalOpen(false);
      setResetText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة التعيين");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h2 className="section-title text-2xl font-black">النسخ الاحتياطي وإدخال البيانات (Backup & Import)</h2>
        <p className="text-xs text-[var(--c-muted)] mt-1">تصدير واسترجاع وإدخال كامل قاعدة بيانات بنك العائلة بحرية وأمان.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* 1. EXPORT CARD */}
        <BaseCard className="space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[var(--c-gold-light)] text-[var(--c-navy)] flex items-center justify-center font-bold">
                <Download className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[var(--c-navy)]">تصدير النسخة الاحتياطية (.json)</h3>
                <p className="text-xs text-[var(--c-muted)]">تحميل ملف JSON منطقي شامل دون كلمات المرور أو الأسرار أو رموز الدخول.</p>
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs text-slate-600">
              يتضمن الإصدار 4.0 بيانات النظام القابلة للاستعادة، مع استبعاد بيانات المصادقة والأسرار الحساسة.
            </div>
          </div>

          <PrimaryButton loading={loading} onClick={downloadBackup} fullWidth>
            <Download className="w-4 h-4" />
            <span>تصدير ملف البيانات الآن (.json)</span>
          </PrimaryButton>
        </BaseCard>

        {/* 2. FILE IMPORT CARD */}
        <BaseCard className="space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                <Upload className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[var(--c-navy)]">استعادة وإدخال ملف بيانات (Import)</h3>
                <p className="text-xs text-[var(--c-muted)]">رفع وإدخال ملف JSON تم تصديره سابقاً لتثبيته بالقاعدة.</p>
              </div>
            </div>

            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
            />

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 hover:border-[var(--c-navy)] p-6 rounded-2xl text-center cursor-pointer transition bg-slate-50/50 hover:bg-slate-50 space-y-2"
            >
              <FileCode className="w-8 h-8 text-[var(--c-muted)] mx-auto" />
              <div className="text-xs font-bold text-[var(--c-navy)]">اضغط هنا لاختيار ملف JSON للنسخة الاحتياطية</div>
              <div className="text-[10px] text-[var(--c-muted)]">أو اسحب واسقط الملف مباشرة</div>
            </div>

            {fileDetails ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs space-y-1 font-num text-emerald-900">
                <div className="font-bold flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>الملف جاهز للاستعادة: {fileDetails.name} ({fileDetails.size})</span>
                </div>
                <div>المستخدمون: <span className="font-bold">{fileDetails.usersCount}</span> | المعاملات: <span className="font-bold">{fileDetails.txsCount}</span> | الإيصالات: <span className="font-bold">{fileDetails.receiptsCount}</span></div>
              </div>
            ) : null}
          </div>

          <PrimaryButton
            disabled={!importData}
            onClick={() => setConfirmModalOpen(true)}
            fullWidth
          >
            <Upload className="w-4 h-4" />
            <span>استعادة وإدخال ملف البيانات المحدد</span>
          </PrimaryButton>
        </BaseCard>
      </div>

      {/* 3. MANUAL JSON TEXT PASTE AREA */}
      <BaseCard className="space-y-4">
        <h3 className="font-bold text-base text-[var(--c-navy)] border-b pb-2">إدخال نص JSON مباشر (Manual JSON Input)</h3>
        <p className="text-xs text-[var(--c-muted)]">يمكنك نسخ ولصق كود JSON مباشرة لإدخال بيانات جديدة أو تحديث السجلات:</p>
        <textarea
          rows="5"
          className="w-full p-3 font-mono text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-[var(--c-navy)] bg-slate-50"
          value={rawJsonText}
          onChange={e => setRawJsonText(e.target.value)}
          placeholder={`{\n  "users": [ ... ],\n  "transactions": [ ... ]\n}`}
        />
        <div className="flex justify-end">
          <SecondaryButton onClick={handleParseRawText} size="sm">
            تحليل وقراءة كود JSON
          </SecondaryButton>
        </div>
      </BaseCard>

      {/* 4. DATABASE RESET & CLEAR DATA */}
      <BaseCard className="border-red-200 bg-red-50/40 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-base text-red-900">إعادة تعيين وتفريغ البيانات (Clear / Reset Data)</h3>
            <p className="text-xs text-red-700">تفريغ جميع المستخدمين والمعاملات لغرض البدء بنسخة نظيفة جديدة.</p>
          </div>
        </div>
        <div className="flex justify-end">
          <DangerButton onClick={() => setResetModalOpen(true)} size="sm">
            إعادة تعيين البيانات الحالية
          </DangerButton>
        </div>
      </BaseCard>

      {/* CONFIRM IMPORT MODAL */}
      <Modal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="تأكيد استعادة وإدخال النسخة الاحتياطية"
        maxWidth="max-w-md"
      >
        <div className="space-y-4 py-2 text-xs font-medium">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 leading-relaxed">
            ⚠️ تنبيه: استعادة النسخة الاحتياطية ستجعل النظام يقرأ ويدخل كافة البيانات المرفقة في الملف. هل أنت متأكد؟
          </div>
          {fileDetails ? (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1 font-num text-slate-700">
              <div>اسم الملف: <span className="font-bold">{fileDetails.name}</span></div>
              <div>عدد المستخدمين المدخلين: <span className="font-bold">{fileDetails.usersCount}</span></div>
              <div>عدد المعاملات المدخلة: <span className="font-bold">{fileDetails.txsCount}</span></div>
            </div>
          ) : null}
          <div className="flex gap-3 pt-2">
            <PrimaryButton loading={loading} onClick={executeImport} fullWidth>
              تأكيد الاستعادة والإدخال
            </PrimaryButton>
            <SecondaryButton disabled={loading} onClick={() => setConfirmModalOpen(false)}>
              إلغاء
            </SecondaryButton>
          </div>
        </div>
      </Modal>

      {/* RESET CONFIRMATION MODAL */}
      <Modal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        title="تأكيد إعادة تعيين قاعدة البيانات"
        maxWidth="max-w-md"
      >
        <div className="space-y-4 py-2 text-xs font-medium">
          <p className="text-red-600 font-bold">
            هذا الإجراء سيقوم بتفريغ كافة جداول المستخدمين والمعاملات والإيصالات.
          </p>
          <div>
            <label className="block text-xs font-bold text-[var(--c-navy)] mb-1">اكتب الكلمة التأكيدية RESET للاستمرار:</label>
            <input
              type="text"
              className="w-full p-2.5 border border-slate-300 rounded-xl font-mono font-bold text-center tracking-widest text-base"
              value={resetText}
              onChange={e => setResetText(e.target.value)}
              placeholder="RESET"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <DangerButton disabled={resetText !== "RESET"} loading={loading} onClick={executeReset} className="flex-1">
              تأكيد التفريغ
            </DangerButton>
            <SecondaryButton disabled={loading} onClick={() => setResetModalOpen(false)}>
              إلغاء
            </SecondaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
